import { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Stack, Skeleton, useTheme, useMediaQuery, Divider, List, ListItem, ListItemText, IconButton, Tooltip, Menu, MenuItem, Typography } from '@mui/material';
import Card from '../common/Card/Card';
import { SubHeading } from '../common/PageHeadings/PageHeadings';
import useAuth from '../../hooks/useAuth';
import axios from '../../api/axios';
import EditIcon from '@mui/icons-material/Edit';
import Button from '../common/Button';
import EditUserModal from '../EditUserModal/EditUserModal';
import { RoleType, User } from '../../@types/Auth';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useConfirmationModalContext } from '../../context/ConfirmModalContext';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LoadingIndicator from '../common/LoadingIndicator/LoadingIndicator';
import CreateUserModal from '../CreateUserModal/CreateUserModal';
import { isDealerAdmin } from '../../utils/isDealerAdmin';
import { isMadJaxAdmin } from '../../utils/isMadJaxAdmin';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';

interface DealerUserListProps {
  dealerId?: string;
}

const tableHeaders = ['name', 'email', 'leads contact', 'status', 'role', ''];

const DealerUserList: FC<DealerUserListProps> = ({ dealerId }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [userToEdit, setUserToEdit] = useState<User | undefined>(undefined);
  const [editUserModalVisible, setEditUserModalVisible] = useState<boolean>(false);
  const [addUserModalVisible, setAddUserModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));
  const confirmContext = useConfirmationModalContext();

  const [contextMenuRowUser, setContextMenuRowUser] = useState<User | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [sendingResetPasswordEmail, setSendingResetPasswordEmail] = useState<boolean>(false);
  const [sendingEmailConfirmation, setSendingEmailConfirmation] = useState<boolean>(false);
  const openMenu = Boolean(anchorElement);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    setContextMenuRowUser(user);
    setAnchorElement(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setContextMenuRowUser(null);
    setAnchorElement(null);
  };

  const getDealerUsers = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = { pk: dealerId ? `DEALERID#${dealerId}` : currentUser?.pk };
      const response = await axios.get(`/dealer/users`, { params: queryParams });
      const companyUsers: User[] = response.data.data as User[];
      setUsers(companyUsers);
    } catch (error) {
      toast.error('Server Error');
    } finally {
      setLoading(false);
    }
  }, [dealerId, currentUser, setUsers]);

  useEffect(() => {
    if (!addUserModalVisible && !editUserModalVisible) {
      getDealerUsers()
    };
  }, [addUserModalVisible, editUserModalVisible, getDealerUsers]);

  const editUser = (userToEdit: User) => {
    setUserToEdit(userToEdit);
    setEditUserModalVisible(true);
  }

  const sendResetPasswordEmail = async () => {
    try {
      setSendingResetPasswordEmail(true);
      const response = await axios.post(`/email/user/password-reset`, { "email": contextMenuRowUser?.email });
      toast.success(response.data.success);
    } catch (error: any) {
      console.log(error);
      toast.error(error.response.data.error);
    } finally {
      setSendingResetPasswordEmail(false);
    }
  };

  const sendEmailConfirmationEmail = async () => {
    try {
      setSendingEmailConfirmation(true);
      const data = { email: contextMenuRowUser?.email }
      const response = await axios.post(`/email/user/register-confirmation`, data);
      toast.success(response.data.data);
    } catch (error: any) {
      console.log(error);
      toast.error(error.response.data.error);
    } finally {
      setSendingEmailConfirmation(false);
    }
  }

  const deleteUser = async (userToDelete: User) => {
    const result = await confirmContext.showConfirmation(
      `Delete User [${userToDelete.email}]`,
      `Would you like to delete user [${userToDelete.email}]?`
    )

    if (result) {
      try {
        setUsers((users) => {
          const userIndex = users.findIndex((user => user.sk === userToDelete.sk));
          users.splice(userIndex, 1);
          return [...users];
        });
        await axios.delete(`/user`, { data: { ...userToDelete } });
        toast.success(`User [${userToDelete.email}] deleted`);
      } catch (error) {
        console.log(error);
        setUsers((users) => [...users, userToDelete]);
        toast.error('Server Error');
      }
    }
  }

  const getUserType = (roleType: RoleType): string => {
    let label = '';
    switch (roleType) {
      case 'dealer-admin':
        label = 'Admin';
        break;
      case 'dealer-sales':
        label = 'Sales & Service';
        break;
      case 'dealer-service':
        label = "Service & Parts";
        break;
      case 'dealer-support':
        label = "Support";
        break;
      case 'admin':
        label = "MadJax Admin";
        break;
      default:
        label = 'Undefined';
    }
    return label;
  }

  return (
    <div>
      <Stack sx={{ marginBottom: '16px' }}>
        <SubHeading>Users</SubHeading>
      </Stack>
      <Card>
        {!loading ?
          ((!isTablet ?
            (
              <Stack direction="column" spacing={2} sx={{ alignItems: 'flex-end' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {tableHeaders.map((header, index) => (
                          <TableCell
                            key={index}
                            sx={{
                              ...((header === 'status'
                                || header === 'role'
                                || header === 'leads contact') && {
                                textAlign: 'center'
                              }),
                              borderLeft: 'none',
                              borderRight: 'none',
                              borderTop: 'none',
                              textTransform: 'uppercase'
                            }}>
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            '.MuiTableCell-root': {
                              border: 'none',
                              paddingTop: '12px',
                              paddingBottom: '12px'
                            }
                          }}>
                          <TableCell>
                            {user.attributes.firstName} {user.attributes.lastName}
                          </TableCell>
                          <TableCell>
                            {user.email}
                          </TableCell>
                          <TableCell sx={{
                            textTransform: "uppercase",
                            textAlign: 'center'
                          }}>
                            {user.attributes.leadsContact
                              ? (

                                <Tooltip title="Leads Contact">

                                  <CheckCircleRoundedIcon sx={{ color: 'green' }} />

                                </Tooltip>

                              )

                              : (

                                <></>

                              )
                            }
                          </TableCell>
                          <TableCell sx={{
                            textTransform: "uppercase",
                            textAlign: 'center'
                          }}>
                            {user.attributes.accountStatus}
                          </TableCell>
                          <TableCell sx={{
                            textTransform: "uppercase",
                            textAlign: 'center'
                          }}>
                            {getUserType(user.attributes.type)}
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              justifyContent="flex-end">
                              {(user.sk !== currentUser?.sk &&
                                (isDealerAdmin(currentUser) || isMadJaxAdmin(currentUser))) &&
                                <Tooltip title="Delete User" disableInteractive>
                                  <IconButton onClick={() => { deleteUser(user) }}>
                                    <DeleteOutlineOutlinedIcon />
                                  </IconButton>
                                </Tooltip>
                              }
                              {(user.sk === currentUser?.sk
                                || (isDealerAdmin(currentUser) || isMadJaxAdmin(currentUser))) &&
                                <Tooltip title="Edit User" disableInteractive>
                                  <IconButton onClick={() => { editUser(user) }}>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              }
                              <Tooltip title="Additional Actions" disableInteractive>
                                <IconButton onClick={(event) => { handleOpenMenu(event, user) }}>
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                              <Menu anchorEl={anchorElement} open={openMenu} onClose={handleCloseMenu}>
                                <MenuItem onClick={sendResetPasswordEmail}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="secondaryFont">
                                      Reset Password Email
                                    </Typography>
                                    <LoadingIndicator size={24} loading={sendingResetPasswordEmail} />
                                  </Stack>
                                </MenuItem>
                                <MenuItem onClick={sendEmailConfirmationEmail}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="secondaryFont">
                                      Send Confirmation Email
                                    </Typography>
                                    <LoadingIndicator size={24} loading={sendingEmailConfirmation} />
                                  </Stack>
                                </MenuItem>
                              </Menu>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {(isDealerAdmin(currentUser) || isMadJaxAdmin(currentUser)) &&
                  <Stack sx={{ width: 'fit-content', paddingLeft: '8px', paddingBottom: '8px' }}>
                    <Button
                      onClick={() => { setAddUserModalVisible(true) }}>
                      Add User
                    </Button>
                  </Stack>
                }
              </Stack>
            )
            :
            (
              <Stack direction="column" spacing={2} divider={<Divider />}>
                {users.map((user, index) => (
                  <List key={index}
                    sx={{
                      '.MuiListItem-root': {
                        paddingLeft: 0,
                        paddingRight: 0,
                        '.MuiListItemText-root:last-of-type': {
                          textAlign: 'right',
                          maxWidth: '50%'
                        }
                      }
                    }}>
                    <ListItem>
                      <ListItemText>First Name</ListItemText>
                      <ListItemText>{user.attributes.firstName}</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>Last Name</ListItemText>
                      <ListItemText>{user.attributes.lastName}</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>Email</ListItemText>
                      <ListItemText sx={{ wordBreak: 'break-all' }}>{user.email}</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>Leads Contact</ListItemText>
                      <ListItemText>{user.attributes.leadsContact ? <CheckCircleRoundedIcon sx={{ color: 'green' }} /> : <DoNotDisturbOnIcon sx={{ color: 'red' }} />}</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>Status</ListItemText>
                      <ListItemText sx={{ wordBreak: 'break-all', textTransform: 'uppercase' }}>{user.attributes.accountStatus}</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>Role</ListItemText>
                      <ListItemText sx={{ textTransform: "uppercase" }}>{getUserType(user.attributes.type)}</ListItemText>
                    </ListItem>
                    <Stack
                      direction="column"
                      spacing={2}>
                      <Button
                        onClick={() => { editUser(user) }}>
                        Edit User
                      </Button>
                    </Stack>
                  </List>
                ))}
                {(isDealerAdmin(currentUser) || isMadJaxAdmin(currentUser)) &&
                  <Button
                    onClick={() => { setAddUserModalVisible(true) }}>
                    Add User
                  </Button>
                }
              </Stack>
            )
          ))
          : (<DealerUserListLoading />)
        }
      </Card>
      <EditUserModal
        user={userToEdit}
        closeModal={() => setEditUserModalVisible(false)}
        isVisible={editUserModalVisible}
        setUsers={setUsers} />
      <CreateUserModal
        dealerId={dealerId}
        closeModal={() => setAddUserModalVisible(false)}
        isVisible={addUserModalVisible} />
    </div >
  );
}

const DealerUserListLoading = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));
  const displayCount = 10;
  return (
    (!isTablet ?
      (
        <TableContainer >
          <Table>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      ...((header === 'status'
                        || header === 'role'
                        || header === 'leads contact') && {
                        textAlign: 'center'
                      }),
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderTop: 'none',
                      textTransform: 'uppercase'
                    }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array(displayCount).fill(null).map((value, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '.MuiTableCell-root': {
                      border: 'none',
                      paddingTop: '12px',
                      paddingBottom: '12px'
                    }
                  }}>
                  <TableCell>
                    <Skeleton animation="wave" width={'100%'} height={20} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" width={'100%'} height={20} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" variant="circular" width={20} height={20} sx={{ marginLeft: 'auto', marginRight: 'auto' }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" width={'100%'} height={20} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" width={'100%'} height={20} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" justifyContent="flex-end" spacing={2}>
                      <Skeleton animation="wave" width={24} height={24} />
                      <Skeleton animation="wave" width={24} height={24} />
                      <Skeleton animation="wave" width={24} height={24} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer >
      )
      :
      (
        <Stack direction="column" spacing={2} divider={<Divider />}>
          {Array(displayCount).fill(null).map((value, index) => (
            <List key={index}
              sx={{
                '.MuiListItem-root': {
                  paddingLeft: 0,
                  paddingRight: 0,
                }
              }}>
              <ListItem>
                <ListItemText>First Name</ListItemText>
                <Skeleton animation="wave" width={'50%'} height={20} />
              </ListItem>
              <ListItem>
                <ListItemText>Last Name</ListItemText>
                <Skeleton animation="wave" width={'50%'} height={20} />
              </ListItem>
              <ListItem>
                <ListItemText>Email</ListItemText>
                <Skeleton animation="wave" width={'50%'} height={20} />
              </ListItem>
              <ListItem>
                <ListItemText>Leads Contact</ListItemText>
                <Skeleton animation="wave" width={'50%'} height={20} />
              </ListItem>
              <ListItem>
                <ListItemText>Status</ListItemText>
                <Skeleton animation="wave" width={'50%'} height={20} />
              </ListItem>
              <ListItem>
                <ListItemText>Type</ListItemText>
                <Skeleton animation="wave" width={'50%'} height={20} />
              </ListItem>
            </List>
          ))}
        </Stack>
      )
    )
  )
}

export default DealerUserList;
